// src/components/admin/InventoryDialog.jsx
import React, { useState, useEffect } from 'react';
import { X, Plus, Minus } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { translations } from '../../utils/translations';
import { inventoryApi, skuApi } from '../../api';

const InventoryDialog = ({ product, onClose, onSuccess }) => {
  const { language } = useApp();
  const t = translations[language];

  const [type, setType] = useState('in'); // 'in' 入库, 'out' 出库
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  // 多规格支持：打开弹窗时先查一遍这个商品有没有规格，
  // 不依赖列表页传过来的 has_specs 标记（可能不是最新的），直接现查一次最准确
  const [specs, setSpecs] = useState([]);
  const [skus, setSkus] = useState([]);
  const [loadingSkus, setLoadingSkus] = useState(true);
  const [selectedSkuId, setSelectedSkuId] = useState('');

  const isMultiSpec = skus.length > 0;
  const selectedSku = skus.find(s => String(s.id) === String(selectedSkuId));

  useEffect(() => {
    let cancelled = false;
    const loadSpecs = async () => {
      setLoadingSkus(true);
      try {
        const res = await skuApi.getProductSpecs(product.id);
        if (!cancelled && res.success && res.data.has_specs) {
          setSpecs(res.data.specs || []);
          setSkus(res.data.skus || []);
        }
      } catch (err) {
        console.error('加载规格失败:', err);
        // 查询失败就当普通单规格商品处理，不阻塞库存调整这个更基础的功能
      } finally {
        if (!cancelled) setLoadingSkus(false);
      }
    };
    loadSpecs();
    return () => { cancelled = true; };
  }, [product.id]);

  // 拼出SKU的规格标签，比如"红色 / S"
  const getSkuLabel = (sku) => {
    if (!sku.spec_values || sku.spec_values.length === 0) return '-';
    return sku.spec_values.map(sv => {
      const spec = specs.find(s => s.id === sv.spec_id);
      const value = spec?.values?.find(v => v.id === sv.spec_value_id);
      return value?.value || '-';
    }).join(' / ');
  };

  // 当前显示的库存：多规格且已选中SKU时，显示这个SKU的库存；
  // 多规格但还没选：显示"--"提示先选规格；单规格：显示商品本身库存
  const currentStock = isMultiSpec
    ? (selectedSku ? selectedSku.stock : null)
    : product.stock;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!quantity || parseInt(quantity) <= 0) {
      alert(language === 'en' ? 'Please enter a valid quantity' :
            language === 'km' ? 'សូមបញ្ចូលចំនួនត្រឹមត្រូវ' :
            '请输入有效的数量');
      return;
    }

    // 多规格商品必须先选一个具体的SKU，不能笼统调整整个商品的库存
    if (isMultiSpec && !selectedSkuId) {
      alert(language === 'en' ? 'Please select a specific variant' :
            language === 'km' ? 'សូមជ្រើសរើសម៉ូដែលជាក់លាក់' :
            '请先选择要调整的具体规格');
      return;
    }

    setLoading(true);

    try {
      const signedQuantity = type === 'in' ? parseInt(quantity) : -parseInt(quantity);
      const changeType = type === 'in' ? 'restock' : 'adjust';
      const notes = reason || (type === 'in' ? '入库' : '出库');

      let res;
      if (isMultiSpec) {
        // 走SKU级别的调整接口，内部会自动同步更新 products.stock 总和，
        // 不会破坏"商品总库存=所有SKU库存之和"这个一致性
        console.log('📦 提交SKU库存调整:', { sku_id: selectedSkuId, quantity: signedQuantity, changeType });
        res = await skuApi.adjustSkuStock(selectedSkuId, {
          quantity: signedQuantity,
          change_type: changeType,
          notes
        });
      } else {
        const adjustmentData = {
          product_id: product.id,
          quantity: signedQuantity,
          change_type: changeType,
          notes
        };
        console.log('📦 提交库存调整:', adjustmentData);
        res = await inventoryApi.adjust(adjustmentData);
      }

      if (res.success) {
        console.log('✅ 库存调整成功');
        alert(language === 'en' ? 'Inventory updated successfully!' :
              language === 'km' ? 'បានធ្វើបច្ចុប្បន្នភាពស្តុកដោយជោគជ័យ!' :
              '库存调整成功！');
        onSuccess();
        onClose();
      } else {
        alert(res.message || t.operationFailed || '操作失败');
      }
    } catch (err) {
      console.error('❌ 库存调整失败:', err);
      alert(err.message || t.operationFailed || '操作失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 获取商品显示名称
  const getProductName = () => {
    switch (language) {
      case 'en':
        return product.name_en || product.name;
      case 'km':
        return product.name_km || product.name;
      default:
        return product.name;
    }
  };

  const outOfRange = type === 'out' && quantity && currentStock != null && parseInt(quantity) > currentStock;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md">
        <div className="border-b p-6 flex justify-between items-center">
          <h3 className="text-xl font-bold">
            {language === 'en' ? 'Inventory Adjustment' :
             language === 'km' ? 'កែតម្រូវស្តុក' :
             '库存调整'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* 商品信息 */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="font-medium text-gray-800">{getProductName()}</p>
                <p className="text-sm text-gray-500">{t.barcode}: {product.barcode}</p>
              </div>
              {!isMultiSpec && (
                <div className="text-right">
                  <p className="text-sm text-gray-600">
                    {language === 'en' ? 'Current Stock' :
                     language === 'km' ? 'ស្តុកបច្ចុប្បន្ន' :
                     '当前库存'}
                  </p>
                  <p className="text-2xl font-bold text-blue-600">{product.stock}</p>
                </div>
              )}
            </div>
          </div>

          {/* 多规格商品：先选具体规格 */}
          {loadingSkus ? (
            <div className="text-center py-4 text-sm text-gray-400">加载规格中...</div>
          ) : isMultiSpec && (
            <div>
              <label className="block text-sm font-medium mb-2">
                选择规格 <span className="text-red-500">*</span>
                <span className="text-xs text-gray-400 ml-1 font-normal">（该商品有多个规格，库存按规格分别管理）</span>
              </label>
              <select
                value={selectedSkuId}
                onChange={e => setSelectedSkuId(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">请选择规格</option>
                {skus.map(sku => (
                  <option key={sku.id} value={sku.id}>
                    {getSkuLabel(sku)}（当前库存：{sku.stock}）
                  </option>
                ))}
              </select>
              {selectedSku && (
                <p className="mt-1 text-sm text-gray-500">
                  当前库存：<span className="font-medium text-blue-600">{selectedSku.stock}</span>
                </p>
              )}
            </div>
          )}

          {/* 操作类型 */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {language === 'en' ? 'Operation Type' :
               language === 'km' ? 'ប្រភេទប្រតិបត្តិការ' :
               '操作类型'}
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setType('in')}
                className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 transition-colors ${
                  type === 'in'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Plus className="w-5 h-5 mr-2" />
                {language === 'en' ? 'Stock In' :
                 language === 'km' ? 'ចូលស្តុក' :
                 '入库'}
              </button>

              <button
                type="button"
                onClick={() => setType('out')}
                className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 transition-colors ${
                  type === 'out'
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Minus className="w-5 h-5 mr-2" />
                {language === 'en' ? 'Stock Out' :
                 language === 'km' ? 'ចេញស្តុក' :
                 '出库'}
              </button>
            </div>
          </div>

          {/* 数量 */}
          <div>
            <label className="block text-sm font-medium mb-1">
              {language === 'en' ? 'Quantity' :
               language === 'km' ? 'ចំនួន' :
               '数量'}
            </label>
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="10"
              min="1"
              required
              disabled={isMultiSpec && !selectedSkuId}
            />
            <p className="mt-1 text-sm text-gray-500">
              {language === 'en' ? 'New Stock' :
               language === 'km' ? 'ស្តុកថ្មី' :
               '调整后库存'}: {
                currentStock == null
                  ? '--'
                  : quantity
                    ? (type === 'in' ? currentStock + parseInt(quantity) : currentStock - parseInt(quantity))
                    : currentStock
              }
            </p>
          </div>

          {/* 备注 */}
          <div>
            <label className="block text-sm font-medium mb-1">
              {language === 'en' ? 'Reason (Optional)' :
               language === 'km' ? 'មូលហេតុ (ស្រេចចិត្ត)' :
               '备注 (可选)'}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              rows="3"
              placeholder={language === 'en' ? 'Enter reason for adjustment...' :
                          language === 'km' ? 'បញ្ចូលមូលហេតុនៃការកែតម្រូវ...' :
                          '输入调整原因...'}
            />
          </div>

          {/* 警告提示 */}
          {outOfRange && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">
                ⚠️ {language === 'en' ? 'Warning: Insufficient stock!' :
                      language === 'km' ? 'ព្រមាន: ស្តុកមិនគ្រប់គ្រាន់!' :
                      '警告: 库存不足！'}
              </p>
            </div>
          )}

          {/* 按钮 */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              {t.cancel}
            </button>
            <button
              type="submit"
              disabled={loading || outOfRange || (isMultiSpec && !selectedSkuId)}
              className={`px-4 py-2 rounded-lg transition-colors ${
                type === 'in'
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-red-600 hover:bg-red-700 text-white'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading
                ? (language === 'en' ? 'Processing...' : language === 'km' ? 'កំពុងដំណើរការ...' : '处理中...')
                : t.confirm}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InventoryDialog;